import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RotateCcw, Check, HelpCircle } from "lucide-react";

const SudokuGame = () => {
    const navigate = useNavigate();
    const [board, setBoard] = useState(Array(9).fill(null).map(() => Array(9).fill(0)));
    const [initialBoard, setInitialBoard] = useState(Array(9).fill(null).map(() => Array(9).fill(0))); // To know which cells are fixed
    const [solutionBoard, setSolutionBoard] = useState(null);
    const [selectedCell, setSelectedCell] = useState(null); // [row, col]
    const [message, setMessage] = useState("");
    const [difficulty, setDifficulty] = useState("Medium");
    const [isWon, setIsWon] = useState(false);
    const [conflicts, setConflicts] = useState(new Set()); // strings "r-c"
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    // Valid seed board to permute
    const baseBoard = [
        [5, 3, 4, 6, 7, 8, 9, 1, 2],
        [6, 7, 2, 1, 9, 5, 3, 4, 8],
        [1, 9, 8, 3, 4, 2, 5, 6, 7],
        [8, 5, 9, 7, 6, 1, 4, 2, 3],
        [4, 2, 6, 8, 5, 3, 7, 9, 1],
        [7, 1, 3, 9, 2, 4, 8, 5, 6],
        [9, 6, 1, 5, 3, 7, 2, 8, 4],
        [2, 8, 7, 4, 1, 9, 6, 3, 5],
        [3, 4, 5, 2, 8, 6, 1, 7, 9]
    ];

    useEffect(() => {
        startNewGame();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [difficulty]);

    // Handle keyboard input
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!selectedCell || isWon) return;
            const [r, c] = selectedCell;

            // Numbers 1-9
            if (e.key >= '1' && e.key <= '9') {
                updateCell(r, c, parseInt(e.key));
            } else if (e.key === 'Backspace' || e.key === 'Delete') {
                updateCell(r, c, 0);
            } else if (e.key.startsWith('Arrow')) {
                moveSelection(e.key);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    });

    const moveSelection = (key) => {
        if (!selectedCell) return;
        let [r, c] = selectedCell;
        if (key === 'ArrowUp') r = Math.max(0, r - 1);
        if (key === 'ArrowDown') r = Math.min(8, r + 1);
        if (key === 'ArrowLeft') c = Math.max(0, c - 1);
        if (key === 'ArrowRight') c = Math.min(8, c + 1);
        setSelectedCell([r, c]);
    };

    const shuffle = (array) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    const generateBoard = () => {
        let newBoard = baseBoard.map(row => [...row]);

        // 1. Shuffle rows within bands
        // Band 0 (rows 0,1,2), Band 1 (3,4,5), Band 2 (6,7,8)
        for (let band = 0; band < 3; band++) {
            const rows = [0, 1, 2].map(i => i + band * 3);
            shuffle(rows);
            const bandRows = rows.map(rIndex => newBoard[rIndex]);
            // Re-assign to new positions (simplified: just reconstructing the block)
            // Easier: Swap rows directly.
            // Let's reload newBoard from the shuffled indices
            // Actually, simpler to just swap rows in place based on the shuffle
             const currentBandStart = band * 3;
             const tempRows = [newBoard[currentBandStart], newBoard[currentBandStart+1], newBoard[currentBandStart+2]];
             // This is tricky to do in-place without copying. 
             // Logic: create a permutation of 0,1,2.
             const p = shuffle([0, 1, 2]);
             newBoard[currentBandStart] = tempRows[p[0]];
             newBoard[currentBandStart+1] = tempRows[p[1]];
             newBoard[currentBandStart+2] = tempRows[p[2]];
        }

        // 2. Transpose (rotate 90 deg) occasionally
        if (Math.random() > 0.5) {
            newBoard = newBoard[0].map((_, colIndex) => newBoard.map(row => row[colIndex]));
        }

        // 3. Shuffle columns within stacks (same logic as rows, but on the now potentially transposed board)
        // ...Skipping for brevity, row shuffle + transpose is usually enough randomness for casual play

        // 4. Remove numbers
        const attempts = difficulty === "Easy" ? 30 : difficulty === "Medium" ? 45 : 55;
        // This is "cells to remove" or "cells to keep"? 
        // Standard clues: Easy ~36-40 clues (remove ~45), Medium ~30-32 clues (remove ~50), Hard ~24-28 (remove ~55)
        // Let's stick to removal count.
        const removeCount = difficulty === "Easy" ? 40 : difficulty === "Medium" ? 50 : 56;

        const puzzleBoard = newBoard.map(row => [...row]);
        let removed = 0;
        const cells = [];
        for(let r=0; r<9; r++) for(let c=0; c<9; c++) cells.push([r,c]);
        shuffle(cells);

        for (let i = 0; i < removeCount; i++) {
            if (i < cells.length) {
                const [r, c] = cells[i];
                puzzleBoard[r][c] = 0;
            }
        }

        return { solved: newBoard, puzzle: puzzleBoard };
    };

    const startNewGame = () => {
        const { puzzle, solved } = generateBoard();
        setBoard(puzzle);
        setSolutionBoard(solved);
        // Deep copy for initial state check
        setInitialBoard(puzzle.map(row => [...row]));
        setIsWon(false);
        setMessage("");
        setConflicts(new Set());
        setSelectedCell(null);
    };

    const updateCell = (r, c, val) => {
        if (initialBoard[r][c] !== 0) return; // Cannot edit initial cells

        const newBoard = board.map(row => [...row]);
        newBoard[r][c] = val;
        setBoard(newBoard);
        
        // Clear specific conflict if fixing it, or just clear all visual conflicts to re-check later
        if (conflicts.size > 0) setConflicts(new Set()); 
        
        checkWin(newBoard);
    };

    const checkWin = (currentBoard) => {
        // Check for 0s
        for (let r = 0; r < 9; r++) {
            for (let c = 0; c < 9; c++) {
                if (currentBoard[r][c] === 0) return;
            }
        }

        // Full board, validate
        if (isValidBoard(currentBoard)) {
            setIsWon(true);
            setMessage("Puzzle Solved! 🎉");
        } else {
            setMessage("Something is not right...");
        }
    };

    const isValidBoard = (currentBoard) => {
        // Build sets for rows, cols, boxes
        const rows = Array(9).fill(0).map(() => new Set());
        const cols = Array(9).fill(0).map(() => new Set());
        const boxes = Array(9).fill(0).map(() => new Set());

        for (let r = 0; r < 9; r++) {
            for (let c = 0; c < 9; c++) {
                const val = currentBoard[r][c];
                const boxIdx = Math.floor(r / 3) * 3 + Math.floor(c / 3);
                
                if (rows[r].has(val) || cols[c].has(val) || boxes[boxIdx].has(val)) return false;
                
                rows[r].add(val);
                cols[c].add(val);
                boxes[boxIdx].add(val);
            }
        }
        return true;
    };

    const handleCheck = () => {
        // Highlight conflicts
        const newConflicts = new Set();
        
        // Rows
        for (let r=0; r<9; r++) {
            const seen = new Map(); // val -> [c]
            for (let c=0; c<9; c++) {
                const val = board[r][c];
                if (val === 0) continue;
                if (!seen.has(val)) seen.set(val, []);
                seen.get(val).push(c);
            }
            seen.forEach((cols, val) => {
                if (cols.length > 1) {
                    cols.forEach(c => newConflicts.add(`${r}-${c}`));
                }
            });
        }

        // Cols
        for (let c=0; c<9; c++) {
            const seen = new Map();
            for (let r=0; r<9; r++) {
                const val = board[r][c];
                if (val === 0) continue;
                if (!seen.has(val)) seen.set(val, []);
                seen.get(val).push(r);
            }
            seen.forEach((rows, val) => {
                if (rows.length > 1) {
                    rows.forEach(r => newConflicts.add(`${r}-${c}`));
                }
            });
        }
        
        // Boxes
        for (let boxr=0; boxr<3; boxr++) {
             for (let boxc=0; boxc<3; boxc++) {
                 const seen = new Map(); // val -> [{r,c}]
                 const startR = boxr*3; 
                 const startC = boxc*3;
                 for(let i=0; i<3; i++) {
                     for(let j=0; j<3; j++) {
                         const r = startR + i;
                         const c = startC + j;
                         const val = board[r][c];
                         if(val === 0) continue;
                         if(!seen.has(val)) seen.set(val, []);
                         seen.get(val).push({r,c});
                     }
                 }
                 seen.forEach((coords, val) => {
                     if(coords.length > 1) {
                         coords.forEach(({r,c}) => newConflicts.add(`${r}-${c}`));
                     }
                 });
             }
        }

        setConflicts(newConflicts);
        if (newConflicts.size === 0) {
             const isFull = board.every(row => row.every(cell => cell !== 0));
             if(!isFull) setMessage("No conflicts found so far.");
             else {
                 setIsWon(true);
                 setMessage("Puzzle Solved! 🎉");
             }
        } else {
            setMessage(`Found ${newConflicts.size} conflicts.`);
        }
    };

    const handleShowSolution = () => {
        if (!isWon) {
            setShowConfirmModal(true);
        }
    };

    const confirmReveal = () => {
        setBoard(solutionBoard.map(row => [...row]));
        setIsWon(true);
        setMessage("Solution Revealed");
        setShowConfirmModal(false);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8 px-4">
            <div className="w-full max-w-lg">
                
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                     <button
                        onClick={() => navigate("/games")}
                        className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                     >
                        <ArrowLeft className="w-5 h-5 mr-1" /> 
                        Back to Games
                     </button>
                     <h1 className="text-3xl font-bold text-gray-800">Sudoku</h1>
                     <div className="w-16"></div> {/* Spacer */}
                </div>

                {/* Controls */}
                <div className="flex justify-between items-center mb-6 bg-white p-3 rounded-xl shadow-sm">
                    <div className="flex space-x-2">
                        {["Easy", "Medium", "Hard"].map(d => (
                            <button
                                key={d}
                                onClick={() => setDifficulty(d)}
                                className={`px-3 py-1 text-sm rounded-lg transition-colors ${difficulty === d ? 'bg-blue-600 text-white font-medium' : 'text-gray-500 hover:bg-gray-100'}`}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                    <div className="flex space-x-2">
                         <button
                            onClick={handleShowSolution}
                            className="text-yellow-600 hover:bg-yellow-50 p-2 rounded-full transition-colors"
                            title="Show Solution"
                        >
                            <HelpCircle className="w-5 h-5" />
                        </button>
                        <button
                            onClick={startNewGame}
                            className="text-blue-600 hover:bg-blue-50 p-2 rounded-full transition-colors"
                            title="New Game"
                        >
                            <RotateCcw className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Board */}
                <div className="aspect-square w-full max-w-[400px] mx-auto bg-gray-600 p-1 border-4 border-gray-800 rounded-lg shadow-xl mb-6">
                    <div className="grid grid-cols-9 h-full bg-gray-300 gap-[1px]">
                        {board.map((row, rIndex) => (
                            row.map((cell, cIndex) => {
                                const isInitial = initialBoard[rIndex][cIndex] !== 0; // Check initial state, NOT current value != 0
                                const isSelected = selectedCell && selectedCell[0] === rIndex && selectedCell[1] === cIndex;
                                const isConflict = conflicts.has(`${rIndex}-${cIndex}`);
                                
                                // Borders for 3x3
                                const borderRight = (cIndex + 1) % 3 === 0 && cIndex !== 8 ? 'border-r-2 border-gray-800' : '';
                                const borderBottom = (rIndex + 1) % 3 === 0 && rIndex !== 8 ? 'border-b-2 border-gray-800' : '';

                                return (
                                    <div
                                        key={`${rIndex}-${cIndex}`}
                                        onClick={() => setSelectedCell([rIndex, cIndex])}
                                        className={`
                                            relative flex items-center justify-center text-xl sm:text-2xl cursor-pointer select-none transition-colors
                                            ${isInitial ? 'bg-gray-100 font-bold text-gray-900' : 'bg-white text-blue-600 font-semibold'}
                                            ${isSelected ? '!bg-blue-100 ring-2 ring-inset ring-blue-500 z-10' : ''}
                                            ${isConflict ? '!bg-red-100 text-red-600' : ''}
                                            ${(cIndex % 3 === 2 && cIndex !== 8) ? 'mr-[2px]' : ''} 
                                            ${(rIndex % 3 === 2 && rIndex !== 8) ? 'mb-[2px]' : ''}
                                        `}
                                        style={{
                                            // Manual gap handling for grid lines if gap doesn't work perfectly
                                            // Actually grid gap is fine, but we want thick lines for boxes.
                                            // Let's rely on simple gap for now, the 3x3 logic is a bit tricky with pure grid-gap.
                                            // Alternative: explicit borders on cells.
                                            borderRight: (cIndex + 1) % 3 === 0 && cIndex !== 8 ? '2px solid #374151' : '1px solid #e5e7eb',
                                            borderBottom: (rIndex + 1) % 3 === 0 && rIndex !== 8 ? '2px solid #374151' : '1px solid #e5e7eb',
                                        }}
                                    >
                                        {cell !== 0 ? cell : ''}
                                    </div>
                                );
                            })
                        ))}
                    </div>
                </div>

                {/* Numpad (Mobile/Convenience) */}
                <div className="grid grid-cols-5 gap-2 max-w-[400px] mx-auto mb-6">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                        <button
                            key={num}
                            onClick={() => selectedCell && updateCell(selectedCell[0], selectedCell[1], num)}
                            className="p-3 bg-white border border-gray-200 rounded-lg shadow-sm text-lg font-bold text-gray-700 hover:bg-gray-50 active:scale-95 transition-all"
                        >
                            {num}
                        </button>
                    ))}
                    <button
                         onClick={handleCheck}
                         className="col-span-1 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 font-bold hover:bg-green-100"
                         title="Check for Errors"
                    >
                        <Check className="mx-auto" />
                    </button>
                </div>

                {/* Message */}
                <div className="h-8 text-center font-bold text-lg mb-4">
                    <span className={isWon ? "text-green-600" : "text-gray-700"}>{message}</span>
                </div>

            </div>
            {/* Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 transform transition-all scale-100">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">Reveal Solution?</h3>
                        <p className="text-gray-600 mb-6">
                            This will give up the current game and show the answer. Are you sure?
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="px-4 py-2 text-gray-600 font-medium hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmReveal}
                                className="px-4 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors shadow-md"
                            >
                                Reveal
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SudokuGame;
